import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  completeConciergeBooking,
  ConciergeAppointmentItem,
  ConciergeSlot,
  getConciergeAppointments,
  getConciergeSlots,
  publishConciergeSlot,
} from "../../../../services/adminCsService";

const formatSlotLabel = (startsAt: string, endsAt: string): string => {
  const start = new Date(startsAt);
  const end = new Date(endsAt);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return startsAt;
  }

  const dateLabel = start.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });

  const startTime = start.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const endTime = end.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateLabel} · ${startTime} – ${endTime}`;
};

const toLocalDatetimeInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const SuperAdminConcierge = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState<ConciergeSlot[]>([]);
  const [appointments, setAppointments] = useState<ConciergeAppointmentItem[]>([]);

  const [startsAt, setStartsAt] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("30");
  const [publishing, setPublishing] = useState(false);

  const [completeTarget, setCompleteTarget] = useState<ConciergeAppointmentItem | null>(
    null
  );
  const [notes, setNotes] = useState("");
  const [completing, setCompleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [slotsResponse, appointmentsResponse] = await Promise.all([
        getConciergeSlots(),
        getConciergeAppointments(),
      ]);

      if (slotsResponse.success) {
        setSlots(slotsResponse.data);
      }

      if (appointmentsResponse.success) {
        setAppointments(appointmentsResponse.data);
      }
    } catch (error: any) {
      toast.error(error?.data || t("admin.concierge.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    document.title = t("admin.concierge.pageTitle");
  }, [t]);

  useEffect(() => {
    const defaultStart = new Date();
    defaultStart.setDate(defaultStart.getDate() + 2);
    defaultStart.setHours(14, 0, 0, 0);
    setStartsAt(toLocalDatetimeInputValue(defaultStart));
    loadData();
  }, [loadData]);

  const handlePublishSlot = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!startsAt) {
      toast.error(t("admin.concierge.validation.startsAtRequired"));
      return;
    }

    setPublishing(true);

    try {
      const startsAtIso = new Date(startsAt).toISOString();

      await publishConciergeSlot({
        starts_at: startsAtIso,
        duration_minutes: Number(durationMinutes) || 30,
      });

      toast.success(t("admin.concierge.toast.publishSuccess"));
      await loadData();
    } catch (error: any) {
      toast.error(error?.data || t("admin.concierge.toast.publishError"));
    } finally {
      setPublishing(false);
    }
  };

  const handleCompleteBooking = async () => {
    if (!completeTarget) {
      return;
    }

    setCompleting(true);

    try {
      await completeConciergeBooking(completeTarget.id, {
        notes: notes.trim() || undefined,
      });

      toast.success(t("admin.concierge.toast.completeSuccess"));
      setCompleteTarget(null);
      setNotes("");
      await loadData();
    } catch (error: any) {
      toast.error(error?.data || t("admin.concierge.toast.completeError"));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.concierge.breadcrumb.title")}
            pageTitle={t("admin.concierge.breadcrumb.pageTitle")}
          />

          <Row>
            <Col xl={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("admin.concierge.publish.title")}</h5>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handlePublishSlot}>
                    <div className="mb-3">
                      <Label htmlFor="concierge-starts-at">
                        {t("admin.concierge.publish.startsAt")}
                      </Label>
                      <Input
                        id="concierge-starts-at"
                        type="datetime-local"
                        value={startsAt}
                        onChange={(event) => setStartsAt(event.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="concierge-duration">
                        {t("admin.concierge.publish.duration")}
                      </Label>
                      <Input
                        id="concierge-duration"
                        type="number"
                        min={15}
                        max={120}
                        step={15}
                        value={durationMinutes}
                        onChange={(event) => setDurationMinutes(event.target.value)}
                      />
                    </div>
                    <Button color="primary" type="submit" disabled={publishing}>
                      {publishing ? <Spinner size="sm" className="me-1" /> : null}
                      {t("admin.concierge.publish.submit")}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            <Col xl={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("admin.concierge.slots.title")}</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="text-muted mb-0">{t("admin.concierge.slots.empty")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("admin.concierge.slots.table.when")}</th>
                            <th>{t("admin.concierge.slots.table.duration")}</th>
                            <th>{t("admin.concierge.slots.table.status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slots.map((slot) => (
                            <tr key={slot.id}>
                              <td>{formatSlotLabel(slot.starts_at, slot.ends_at)}</td>
                              <td>
                                {t("admin.concierge.slots.durationValue", {
                                  minutes: slot.duration_minutes,
                                })}
                              </td>
                              <td>
                                <Badge color={slot.is_available ? "success" : "secondary"}>
                                  {slot.is_available
                                    ? t("admin.concierge.slots.available")
                                    : t("admin.concierge.slots.booked")}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-1">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("admin.concierge.appointments.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : appointments.length === 0 ? (
                    <p className="text-muted mb-0">{t("admin.concierge.appointments.empty")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("admin.concierge.appointments.table.tenant")}</th>
                            <th>{t("admin.concierge.appointments.table.slot")}</th>
                            <th>{t("admin.concierge.appointments.table.status")}</th>
                            <th>{t("admin.concierge.appointments.table.action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointments.map((appointment) => (
                            <tr key={appointment.id}>
                              <td>
                                <div className="fw-medium">{appointment.tenant_name}</div>
                                <div className="text-muted small">
                                  {appointment.booked_by?.email ?? "—"}
                                </div>
                              </td>
                              <td>
                                {appointment.slot
                                  ? formatSlotLabel(
                                      appointment.slot.starts_at,
                                      appointment.slot.ends_at
                                    )
                                  : "—"}
                              </td>
                              <td>
                                <Badge
                                  color={
                                    appointment.status === "completed"
                                      ? "success"
                                      : appointment.status === "booked"
                                        ? "primary"
                                        : "secondary"
                                  }
                                >
                                  {t(
                                    `admin.concierge.appointments.status.${appointment.status}`,
                                    { defaultValue: appointment.status }
                                  )}
                                </Badge>
                              </td>
                              <td>
                                {appointment.status === "booked" ? (
                                  <Button
                                    size="sm"
                                    color="soft-success"
                                    onClick={() => {
                                      setCompleteTarget(appointment);
                                      setNotes("");
                                    }}
                                  >
                                    {t("admin.concierge.appointments.complete")}
                                  </Button>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={completeTarget !== null} toggle={() => setCompleteTarget(null)} centered>
        <ModalHeader toggle={() => setCompleteTarget(null)}>
          {t("admin.concierge.completeModal.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {t("admin.concierge.completeModal.description", {
              tenant: completeTarget?.tenant_name ?? "—",
            })}
          </p>
          <Label htmlFor="concierge-complete-notes">
            {t("admin.concierge.completeModal.notes")}
          </Label>
          <Input
            id="concierge-complete-notes"
            type="textarea"
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t("admin.concierge.completeModal.notesPlaceholder")}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCompleteTarget(null)} disabled={completing}>
            {t("admin.concierge.completeModal.cancel")}
          </Button>
          <Button color="success" onClick={handleCompleteBooking} disabled={completing}>
            {completing ? <Spinner size="sm" className="me-1" /> : null}
            {t("admin.concierge.completeModal.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SuperAdminConcierge;
